import { useEffect, useState } from "react";
import classNames from "classnames";
import moment from "moment";
import { Button } from "../buttons/button";
import { PopoverMenu } from "../dropdown/popover";
import { Icon } from "../icon/icon";
import { DateCalendar } from "@mui/x-date-pickers";
import { DateFilter } from "../../utils/types";

export const CustomDateComponent = ({
  value,
  onChange,
}: {
  value?: {
    filter?: DateFilter;
    startDate?: string;
    endDate?: string;
  };
  onChange: (range: {
    filter?: DateFilter;
    startDate?: string;
    endDate?: string;
  }) => void;
}) => {
  const [view, setView] = useState<
    "default" | "custom" | "start-picker" | "end-picker"
  >("default");
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<moment.Moment | null>(
    value?.startDate ? moment(value.startDate) : null,
  );
  const [endDate, setEndDate] = useState<moment.Moment | null>(
    value?.endDate ? moment(value.endDate) : null,
  );

  const presets: { label: string; key: DateFilter | "custom" }[] = [
    { label: "All Time", key: "all" },
    { label: "Today", key: "today" },
    { label: "Yesterday", key: "yesterday" },
    { label: "This Week", key: "this-week" },
    { label: "This Month", key: "this-month" },
    { label: "Custom Range", key: "custom" },
  ];
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handlePresetClick = (key: string, close: () => void) => {
    if (key === "custom") {
      setView("custom");
      return;
    }

    onChange({
      filter: key as DateFilter,
      startDate: undefined,
      endDate: undefined,
    });
    close();
    setView("default");
  };

  useEffect(() => {
    if (startDate && endDate && !startDate.isAfter(endDate)) {
      setError(null);
    }
  }, [startDate, endDate]);

  const getButtonLabel = () => {
    if (value?.filter === "all") {
      return "All Time";
    }
    if (value?.filter && value.filter !== "custom") {
      return value.filter.replace(/-/g, " ");
    }
    if (value?.filter === "custom" && value?.startDate && value?.endDate) {
      return "Custom Range";
    }
    if (value?.startDate && value?.endDate) {
      return "Custom Range";
    }
    return "All Time";
  };

  const isActive = value?.filter || (value?.startDate && value?.endDate);

  const calendarSx = {
    height: "290px",
    "& .MuiPickersCalendarHeader-root": { display: "none" },
    "& .MuiDayCalendar-weekContainer": {
      marginBottom: 0,
      justifyContent: "space-between",
    },
    "& .MuiDayCalendar-weekDayLabel": {
      fontFamily: "Fustat, sans-serif",
      fontWeight: 500,
      width: "100%",
      fontSize: "14px",
      color: "#7081A0",
      marginTop: "8px",
    },
    "& .MuiPickersDay-root": {
      fontFamily: "Fustat, sans-serif",
      fontWeight: 600,
      fontSize: "14px",
      color: "#091525",
    },
    "& .MuiPickersDay-dayOutsideMonth, & .MuiPickersDay-root.Mui-disabled": {
      color: "#A9BBD1 !important",
      fontWeight: 600,
      fontSize: "14px",
    },
    "& .MuiPickersDay-root.Mui-selected": {
      backgroundColor: "#E7ECF2 !important",
      color: "#091525 !important",
      borderRadius: "8px",
      border: "transparent",
    },
    "& .MuiPickersDay-today": {
      borderRadius: "100%",
    },
  };

  useEffect(() => {
    if (!isOpen) {
      setView("default");
    }
  }, [isOpen]);

  return (
    <PopoverMenu
      className="min-w-max"
      position="bottom"
      menuClassName="!min-w-fit !z-10 rounded-[8px] bg-white border border-grey-100 !shadow-sm ![--anchor-gap:6px]"
      customMenuItems={({ close }) => {
        return (
          <div className="flex w-[340px] flex-col overflow-hidden rounded-xl bg-white">
            {view === "default" && (
              <>
                {presets.map((preset, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handlePresetClick(preset.key, close)}
                    className="w-full border-b border-b-grey-100 px-4 py-3 text-left text-sm last:border-b-0"
                  >
                    <span className={classNames("font-bold text-grey-950")}>
                      {preset.label}
                    </span>
                  </button>
                ))}
              </>
            )}

            {view === "custom" && (
              <div className="flex flex-col gap-3 p-4">
                <div className="flex items-center gap-2">
                  <Icon
                    svg="arrow-left-two"
                    width={24}
                    height={24}
                    onClick={() => setView("default")}
                  />
                  <p className="text-base font-bold text-dark-blue-500">
                    Custom Range
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  <main className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => setView("start-picker")}
                      className={`flex w-full items-center justify-between rounded-[8px] border bg-grey-50 px-2.5 py-2 ${error ? "border-danger" : "border-grey-100"}`}
                    >
                      <div className="flex flex-col gap-1">
                        <p className="text-left text-[11px] text-grey-700">
                          Start Date
                        </p>
                        <span
                          className={`text-sm leading-[14px] ${
                            startDate
                              ? "font-bold text-grey-950"
                              : "text-grey-500"
                          } `}
                        >
                          {startDate
                            ? startDate.format("DD/MM/YYYY")
                            : "DD/MM/YYYY"}
                        </span>
                      </div>
                      <Icon
                        svg="calendar-2"
                        width={24}
                        height={24}
                        className="text-grey-800"
                      />
                    </button>
                    {error && (
                      <p className="text-xs font-medium text-danger">
                        {error}
                      </p>
                    )}
                  </main>

                  <button
                    type="button"
                    onClick={() => setView("end-picker")}
                    className={`flex w-full items-center justify-between rounded-[8px] border border-grey-100 bg-grey-50 px-2.5 py-2`}
                  >
                    <div className="flex flex-col gap-1">
                      <p className="text-left text-[11px] text-grey-700">
                        End Date
                      </p>
                      <span
                        className={`text-sm leading-[14px] ${
                          endDate ? "font-bold text-grey-950" : "text-grey-500"
                        } `}
                      >
                        {endDate ? endDate.format("DD/MM/YYYY") : "DD/MM/YYYY"}
                      </span>
                    </div>
                    <Icon
                      svg="calendar-2"
                      width={24}
                      height={24}
                      className="text-grey-800"
                    />
                  </button>
                </div>
                <Button
                  type="button"
                  variation="primary"
                  text="Apply"
                  disabled={!startDate || !endDate}
                  onClick={() => {
                    if (!startDate || !endDate) return;

                    if (startDate.isAfter(endDate)) {
                      setError("Start date cannot be later than end date");
                      return;
                    }
                    setError(null);
                    onChange({
                      filter: "custom",
                      startDate: startDate.format("YYYY-MM-DD"),
                      endDate: endDate.format("YYYY-MM-DD"),
                    });
                    close();
                    setView("default");
                  }}
                />
              </div>
            )}

            {view === "start-picker" && (
              <div className="flex flex-col">
                <div className="flex items-center border-y border-grey-200 p-3">
                  <Icon
                    svg="caret-down"
                    width={20}
                    height={20}
                    className="rotate-90 cursor-pointer text-grey-500"
                    onClick={() =>
                      setStartDate((prev) =>
                        moment(prev || moment()).subtract(1, "month"),
                      )
                    }
                  />
                  <p className="flex-1 text-center font-[Fustat] text-base font-bold text-primary">
                    {moment(startDate || moment()).format("MMMM YYYY")}
                  </p>
                  <Icon
                    svg="caret-down"
                    width={20}
                    height={20}
                    className="-rotate-90 cursor-pointer text-grey-500"
                    onClick={() =>
                      setStartDate((prev) =>
                        moment(prev || moment()).add(1, "month"),
                      )
                    }
                  />
                </div>

                <DateCalendar
                  value={startDate || moment()}
                  onChange={(newDate) => {
                    if (newDate) {
                      setStartDate(moment(newDate));
                      setView("custom");
                    }
                  }}
                  views={["day"]}
                  showDaysOutsideCurrentMonth
                  reduceAnimations
                  maxDate={moment()}
                  dayOfWeekFormatter={(day: any) =>
                    String(day).charAt(0).toUpperCase() +
                    String(day).charAt(1).toLowerCase()
                  }
                  sx={calendarSx}
                />
              </div>
            )}

            {view === "end-picker" && (
              <div className="flex flex-col">
                <div className="flex items-center border-y border-grey-200 p-2">
                  <Icon
                    svg="caret-down"
                    width={20}
                    height={20}
                    className="rotate-90 cursor-pointer text-grey-500"
                    onClick={() =>
                      setEndDate((prev) =>
                        moment(prev || moment()).subtract(1, "month"),
                      )
                    }
                  />
                  <p className="flex-1 text-center font-[Fustat] text-base font-bold text-primary">
                    {moment(endDate || moment()).format("MMMM YYYY")}
                  </p>
                  <Icon
                    svg="caret-down"
                    width={20}
                    height={20}
                    className="-rotate-90 cursor-pointer text-grey-500"
                    onClick={() =>
                      setEndDate((prev) =>
                        moment(prev || moment()).add(1, "month"),
                      )
                    }
                  />
                </div>

                <DateCalendar
                  value={endDate || moment()}
                  minDate={startDate || undefined}
                  onChange={(newDate) => {
                    if (newDate) {
                      setEndDate(moment(newDate));
                      setView("custom");
                    }
                  }}
                  views={["day"]}
                  showDaysOutsideCurrentMonth
                  reduceAnimations
                  maxDate={moment()}
                  dayOfWeekFormatter={(day: any) =>
                    String(day).charAt(0).toUpperCase() +
                    String(day).charAt(1).toLowerCase()
                  }
                  sx={calendarSx}
                />
              </div>
            )}
          </div>
        );
      }}
    >
      {({ open }) => {
        if (open !== isOpen) {
          setIsOpen(open);
        }
        return (
          <Button
            type="button"
            text={getButtonLabel()}
            icon="caret-down"
            iconFirst={false}
            iconDimension={20}
            variation="styled-grey"
            className={`capitalize ${open || isActive ? "bg-grey-50" : ""}`}
          />
        );
      }}
    </PopoverMenu>
  );
};
